/**
 * File type detection utilities for sandbox-generated files.
 *
 * Used by ToolUseBlock and FileOutputCard to determine how to render
 * files created by the agent (inline preview vs download-only).
 */

export type FileCategory =
  | "pdf"
  | "image"
  | "video"
  | "audio"
  | "document"
  | "code"
  | "html"
  | "text"
  | "other";

const BINARY_EXTENSIONS = new Set([
  // Documents
  "pdf",
  "pptx",
  "docx",
  "xlsx",
  // Images
  "png",
  "jpg",
  "jpeg",
  "gif",
  "svg",
  "webp",
  "ico",
  "bmp",
  // Video
  "mp4",
  "webm",
  "mov",
  "avi",
  // Audio
  "mp3",
  "wav",
  "ogg",
  "m4a",
  "flac",
  // Archives
  "zip",
  "tar",
  "gz",
  "rar",
  "7z",
]);

const CATEGORY_MAP: Record<string, FileCategory> = {
  pdf: "pdf",
  // Images
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  svg: "image",
  webp: "image",
  ico: "image",
  bmp: "image",
  // Video
  mp4: "video",
  webm: "video",
  mov: "video",
  avi: "video",
  // Audio
  mp3: "audio",
  wav: "audio",
  ogg: "audio",
  m4a: "audio",
  flac: "audio",
  // Office documents
  pptx: "document",
  docx: "document",
  xlsx: "document",
  // Code (non-binary, but distinct from plain text)
  js: "code",
  jsx: "code",
  ts: "code",
  tsx: "code",
  py: "code",
  rb: "code",
  go: "code",
  rs: "code",
  java: "code",
  kt: "code",
  swift: "code",
  c: "code",
  cpp: "code",
  h: "code",
  cs: "code",
  php: "code",
  css: "code",
  scss: "code",
  sql: "code",
  sh: "code",
  bash: "code",
  // HTML — own category for inline iframe preview (drops skill output)
  html: "html",
  htm: "html",
  // Text
  txt: "text",
  md: "text",
  csv: "text",
  json: "text",
  yaml: "text",
  yml: "text",
  xml: "text",
  toml: "text",
  ini: "text",
  env: "text",
};

const MIME_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  csv: "text/csv",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  svg: "image/svg+xml",
  webp: "image/webp",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  m4a: "audio/mp4",
  zip: "application/zip",
  html: "text/html",
  htm: "text/html",
  json: "application/json",
  md: "text/markdown",
  txt: "text/plain",
};

export function getExtension(filePath: string): string {
  const fileName = filePath.split("/").pop() || "";
  const ext = fileName.includes(".")
    ? fileName.split(".").pop()?.toLowerCase() || ""
    : "";
  return ext;
}

export function getFileCategory(filePath: string): FileCategory {
  const ext = getExtension(filePath);
  return CATEGORY_MAP[ext] || "other";
}

export function isPreviewable(filePath: string): boolean {
  const category = getFileCategory(filePath);
  return (
    category === "pdf" ||
    category === "image" ||
    category === "video" ||
    category === "audio" ||
    category === "html"
  );
}

export function isBinaryFile(filePath: string): boolean {
  const ext = getExtension(filePath);
  return BINARY_EXTENSIONS.has(ext);
}

export function getMimeType(filePath: string): string {
  const ext = getExtension(filePath);
  return MIME_TYPES[ext] || "application/octet-stream";
}
